import { useState, useEffect, useCallback, useRef } from 'react';
import PhaserAvatar from '../components/PhaserAvatar';
import './AvatarPage.css';

// Item types and display labels
const EQUIP_TYPES = ['top', 'bottom', 'accessory', 'handheld'];
const ALL_TYPES   = ['top', 'bottom', 'accessory', 'handheld', 'animation', 'emote', 'bg'];
const TYPE_LABELS = {
  top: 'Top', bottom: 'Bottom', accessory: 'Accessory', handheld: 'Handheld',
  animation: 'Animation', emote: 'Emote', bg: 'Background',
};
const RARITY_ORDER = { common: 0, rare: 1, epic: 2, legendary: 3 };
const RARITY_COLORS = { common: '#68d391', rare: '#63b3ed', epic: '#b794f4', legendary: '#f6e05e' };

// ── Lightweight item thumbnail (no Phaser — just CSS) ────────────────────────
const TYPE_ICONS = {
  top: '👕', bottom: '👖', accessory: '🎩', handheld: '🌿',
  animation: '✨', emote: '😄', bg: '🌅', base_model: '🧍',
};
const RARITY_BG = {
  common: '#12261a', rare: '#111a2e', epic: '#1a1226', legendary: '#261f08',
};
const RARITY_BORDER = {
  common: '#38a169', rare: '#4299e1', epic: '#9f7aea', legendary: '#d69e2e',
};
function ItemThumb({ item, size = 52 }) {
  return (
    <div style={{
      width: size, height: size,
      background: RARITY_BG[item.rarity] || RARITY_BG.common,
      border: `2px solid ${RARITY_BORDER[item.rarity] || RARITY_BORDER.common}`,
      borderRadius: 8,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: Math.round(size * 0.38),
      flexShrink: 0,
    }}>
      {TYPE_ICONS[item.type] || '⭐'}
    </div>
  );
}

function TierBadge({ rarity }) {
  return (
    <span className="avatar-tier-badge" style={{ color: RARITY_COLORS[rarity] || '#fff' }}>
      {rarity}
    </span>
  );
}

function TokenChip({ balance }) {
  return (
    <div className="avatar-token-chip">
      <span>🪙</span>
      <span className="avatar-token-balance">{balance}</span>
      <span className="avatar-token-label">tokens</span>
    </div>
  );
}

export default function AvatarPage() {
  const [tab, setTab]               = useState('builder');
  const [catalog, setCatalog]       = useState([]);
  const [inventory, setInventory]   = useState([]);
  const [loadout, setLoadout]       = useState(null);
  const [draft, setDraft]           = useState(null);   // unsaved equip changes
  const [tokenBalance, setBalance]  = useState(0);
  const [activeType, setActiveType] = useState('top');
  const [saving, setSaving]         = useState(false);
  const [buying, setBuying]         = useState(null);
  const [error, setError]           = useState('');
  const [success, setSuccess]       = useState('');
  const [playingEmote, setPlaying]  = useState(false);
  const sceneRef = useRef(null);

  const fetchAll = useCallback(async () => {
    try {
      const [catRes, invRes, loadRes, tokRes] = await Promise.all([
        fetch('/api/avatar/catalog'),
        fetch('/api/avatar/inventory'),
        fetch('/api/avatar/loadout'),
        fetch('/api/avatar/tokens'),
      ]);
      const [cat, inv, load, tok] = await Promise.all([
        catRes.json(), invRes.json(), loadRes.json(), tokRes.json(),
      ]);
      setCatalog(Array.isArray(cat) ? cat : []);
      setInventory(Array.isArray(inv) ? inv : []);
      setLoadout(load);
      setDraft(load);
      setBalance(tok.balance || 0);
    } catch {
      setError('Failed to load avatar data');
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Build avatarConfig for Phaser from draft loadout
  const avatarConfig = draft
    ? {
        base_model: draft.base_model || 'base_default',
        top:        draft.top || null,
        bottom:     draft.bottom || null,
        accessory:  draft.accessory || null,
        handheld:   draft.handheld || null,
        animation:  draft.animation || 'idle',
        emotes:     draft.emotes || [],
        bg:         draft.bg || null,
      }
    : { base_model: 'base_default' };

  function equipItem(item) {
    const slot = EQUIP_TYPES.includes(item.type) ? item.type : null;
    if (slot) {
      setDraft(prev => ({ ...(prev || {}), [slot]: { id: item.id, item_key: item.item_key, name: item.name, rarity: item.rarity } }));
    } else if (item.type === 'animation') {
      setDraft(prev => ({ ...(prev || {}), animation: item.item_key }));
    } else if (item.type === 'bg') {
      setDraft(prev => ({ ...(prev || {}), bg: { id: item.id, item_key: item.item_key, name: item.name } }));
    } else if (item.type === 'emote') {
      // Add emote to list (max 6)
      setDraft(prev => {
        const emotes = prev?.emotes || [];
        if (emotes.some(e => e.item_key === item.item_key)) return prev;
        return { ...prev, emotes: [...emotes.slice(0, 5), { id: item.id, item_key: item.item_key, name: item.name }] };
      });
    }
  }

  function unequipSlot(slot) {
    if (EQUIP_TYPES.includes(slot)) {
      setDraft(prev => ({ ...(prev || {}), [slot]: null }));
    } else if (slot === 'animation') {
      setDraft(prev => ({ ...(prev || {}), animation: 'idle' }));
    } else if (slot === 'bg') {
      setDraft(prev => ({ ...(prev || {}), bg: null }));
    }
  }

  function removeEmote(itemKey) {
    setDraft(prev => ({ ...(prev || {}), emotes: (prev?.emotes || []).filter(e => e.item_key !== itemKey) }));
  }

  async function saveLoadout() {
    setSaving(true);
    setError('');
    try {
      const body = {
        base_model: draft?.base_model || 'base_default',
        top:        draft?.top?.id        || null,
        bottom:     draft?.bottom?.id     || null,
        accessory:  draft?.accessory?.id  || null,
        handheld:   draft?.handheld?.id   || null,
        animation:  draft?.animation      || 'idle',
        emotes:     draft?.emotes || [],
        bg:         draft?.bg?.id         || null,
      };
      const res = await fetch('/api/avatar/loadout', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) { const d = await res.json(); setError(d.error || 'Save failed'); return; }
      const updated = await res.json();
      setLoadout(updated);
      setDraft(updated);
      setSuccess('Loadout saved!');
      setTimeout(() => setSuccess(''), 2500);
    } catch { setError('Save failed'); }
    finally { setSaving(false); }
  }

  async function buyItem(item) {
    setBuying(item.id);
    setError('');
    try {
      const res = await fetch('/api/avatar/buy', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ itemId: item.id }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error || 'Purchase failed'); return; }
      setBalance(data.avatar_tokens);
      setSuccess(`Purchased: ${item.name}!`);
      setTimeout(() => setSuccess(''), 2500);
      await fetchAll();
    } catch { setError('Purchase failed'); }
    finally { setBuying(null); }
  }

  function triggerEmote(emoteKey) {
    const scene = sceneRef.current;
    if (!scene || playingEmote) return;
    setPlaying(true);
    scene.playEmote(emoteKey, () => setPlaying(false));
  }

  // Helpers
  const ownedForType = inventory.filter(i => i.type === activeType);
  const catalogByType = ALL_TYPES.reduce((acc, t) => {
    acc[t] = catalog
      .filter(i => i.type === t)
      .sort((a, b) => (RARITY_ORDER[a.rarity] || 0) - (RARITY_ORDER[b.rarity] || 0) || a.sort_order - b.sort_order);
    return acc;
  }, {});

  const equippedKeyFor = (type) => {
    if (EQUIP_TYPES.includes(type)) return draft?.[type]?.item_key;
    if (type === 'animation')       return draft?.animation;
    if (type === 'bg')              return draft?.bg?.item_key;
    return null;
  };

  return (
    <div className="avatar-page">
      <div className="avatar-page-header">
        <h2>Avatar</h2>
        <TokenChip balance={tokenBalance} />
      </div>

      {error   && <div className="avatar-error">{error}</div>}
      {success && <div className="avatar-success">{success}</div>}

      <div className="avatar-tabs">
        {['builder', 'store', 'inventory'].map(t => (
          <button key={t} className={`avatar-tab-btn${tab === t ? ' active' : ''}`} onClick={() => setTab(t)}>
            {t.charAt(0).toUpperCase() + t.slice(1)}
          </button>
        ))}
      </div>

      {/* ── BUILDER ──────────────────────────────────────────────────────── */}
      {tab === 'builder' && (
        <div className="builder-layout">

          {/* Full-width landscape stage */}
          <div className="builder-stage">
            <div className="builder-stage-floor" />
            <div className="builder-stage-avatar">
              <PhaserAvatar
                avatarConfig={avatarConfig}
                size={320}
                animated
                onSceneReady={scene => { sceneRef.current = scene; }}
              />
            </div>
            {/* Emote buttons — right side of stage */}
            <div className="builder-stage-emotes">
              {(draft?.emotes || []).map(e => (
                <button
                  key={e.item_key}
                  className={`emote-btn${playingEmote ? ' disabled' : ''}`}
                  onClick={() => triggerEmote(e.item_key)}
                  disabled={playingEmote}
                  title={e.name}
                >
                  {e.item_key === 'emote_wave'  ? '👋' :
                   e.item_key === 'emote_jump'  ? '🦘' :
                   e.item_key === 'emote_dance' ? '🕺' :
                   e.item_key === 'emote_smoke' ? '💨' :
                   e.item_key === 'emote_peace' ? '✌️' : '⭐'}
                </button>
              ))}
              {(draft?.emotes || []).length === 0 && (
                <span className="emote-hint">Add emotes<br/>from inventory</span>
              )}
            </div>
          </div>

          {/* Controls bar: equipped summary + save */}
          <div className="builder-controls">
            <div className="builder-equipped-list">
              {[...EQUIP_TYPES, 'animation', 'bg'].map(slot => {
                const key = equippedKeyFor(slot);
                const name = slot === 'animation'
                  ? (draft?.animation && draft.animation !== 'idle' ? draft.animation.replace('anim_', '') : 'Idle')
                  : draft?.[slot]?.name || (slot === 'bg' ? 'Plain' : null);
                return (
                  <div key={slot} className="builder-equipped-row">
                    <span className="builder-slot-label">{TYPE_LABELS[slot]}</span>
                    <span className="builder-slot-value">{name || <em>none</em>}</span>
                    {key && key !== 'idle' && (
                      <button className="btn-unequip" onClick={() => unequipSlot(slot)}>✕</button>
                    )}
                  </div>
                );
              })}
              {(draft?.emotes || []).map(e => (
                <span key={e.item_key} className="equipped-emote-chip">
                  {e.name}
                  <button className="btn-unequip-small" onClick={() => removeEmote(e.item_key)}>✕</button>
                </span>
              ))}
            </div>
            <button className="btn btn-primary builder-save-btn" onClick={saveLoadout} disabled={saving}>
              {saving ? 'Saving…' : 'Save Loadout'}
            </button>
          </div>

          {/* Item picker */}
          <div className="builder-slot-panel">
            <div className="builder-slot-tabs">
              {ALL_TYPES.map(t => (
                <button
                  key={t}
                  className={`builder-slot-tab${activeType === t ? ' active' : ''}`}
                  onClick={() => setActiveType(t)}
                >
                  {TYPE_LABELS[t]}
                </button>
              ))}
            </div>
            <div className="builder-items-grid">
              {ownedForType.length === 0 && (
                <p className="builder-empty">Nothing owned for {TYPE_LABELS[activeType]}.<br />Visit the Store!</p>
              )}
              {ownedForType.map(item => {
                const equipped = equippedKeyFor(item.type) === item.item_key
                  || (item.type === 'emote' && (draft?.emotes || []).some(e => e.item_key === item.item_key));
                return (
                  <button
                    key={item.id}
                    className={`avatar-item-card${equipped ? ' equipped' : ''}`}
                    onClick={() => equipped && item.type !== 'emote' ? unequipSlot(item.type) : equipItem(item)}
                  >
                    <ItemThumb item={item} size={52} />
                    <div className="avatar-item-info">
                      <span className="avatar-item-name">{item.name}</span>
                      <TierBadge rarity={item.rarity} />
                    </div>
                    {equipped && <div className="equipped-check">✓</div>}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* ── STORE ─────────────────────────────────────────────────────────── */}
      {tab === 'store' && (
        <div className="store-layout">
          <div className="store-token-bar">
            <TokenChip balance={tokenBalance} />
            <span className="store-hint">Complete achievements to earn tokens</span>
          </div>

          {ALL_TYPES.map(type => {
            const items = catalogByType[type];
            if (!items.length) return null;
            return (
              <div key={type} className="store-section">
                <h3 className="store-section-title">{TYPE_LABELS[type]}</h3>
                <div className="store-grid">
                  {items.map(item => (
                    <div key={item.id} className={`store-item-card${item.owned ? ' owned' : ''}`}>
                      <div className="store-item-preview">
                        <ItemThumb item={item} size={60} />
                      </div>
                      <div className="store-item-details">
                        <span className="store-item-name">{item.name}</span>
                        <TierBadge rarity={item.rarity} />
                        {item.description && <span className="store-item-desc">{item.description}</span>}
                        {item.unlock_level && !item.owned && (
                          <span className="store-item-unlock">Unlocks at level {item.unlock_level}</span>
                        )}
                      </div>
                      <div className="store-item-action">
                        {item.owned ? (
                          <span className="store-owned-badge">Owned</span>
                        ) : item.unlock_source === 'level' ? (
                          <span className="store-locked-badge">Lv {item.unlock_level}</span>
                        ) : item.token_cost === 0 ? (
                          <span className="store-free-badge">Free</span>
                        ) : (
                          <button
                            className="btn btn-primary store-buy-btn"
                            onClick={() => buyItem(item)}
                            disabled={buying === item.id || tokenBalance < item.token_cost}
                          >
                            {buying === item.id ? '…' : `🪙 ${item.token_cost}`}
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── INVENTORY ─────────────────────────────────────────────────────── */}
      {tab === 'inventory' && (
        <div className="inventory-layout">
          {ALL_TYPES.map(type => {
            const items = inventory.filter(i => i.type === type);
            if (!items.length) return null;
            return (
              <div key={type} className="inventory-section">
                <h3 className="inventory-section-title">{TYPE_LABELS[type]}</h3>
                <div className="inventory-grid">
                  {items.map(item => (
                    <div key={item.id} className="inventory-item-card">
                      <ItemThumb item={item} size={48} />
                      <div className="inventory-item-info">
                        <span className="inventory-item-name">{item.name}</span>
                        <TierBadge rarity={item.rarity} />
                        <span className="inventory-item-source">{(item.source || '').replace('_', ' ')}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
          {inventory.length === 0 && (
            <p className="inventory-empty">Your inventory is empty. Visit the Store!</p>
          )}
        </div>
      )}
    </div>
  );
}
